import { z } from 'zod';
/**
 * Flappy — a seeded side-scroller. Both players fly the SAME course; first to
 * crash loses, the survivor wins the segment (both alive at the cap = draw).
 *
 * The client runs smooth local physics for feel but is NOT trusted: it streams
 * flap timestamps and its crash, and the server replay-verifies the run against
 * the seeded pipe layout, rejecting impossible survival.
 */
/**
 * World tuning — shared by client sim and server verifier so both agree.
 *
 * Calibrated to the canonical Flappy feel (gravity ≈ 750, flap ≈ -280 scaled to
 * this 640px world): the bird takes ~1.5s to fall the screen and a flap lifts it
 * a bit under a third of it. Terminal velocity caps the fall so it never
 * "drops at light speed".
 */
export const GRAVITY = 1100; // px/s^2 (downward)
export const FLAP_VELOCITY = -400; // px/s one-shot impulse (up is negative)
export const MAX_FALL_SPEED = 560; // terminal velocity (px/s), caps the descent
export const SCROLL_SPEED = 130; // px/s horizontal — a calmer pace
export const PIPE_GAP = 150; // MAX vertical gap height (px) — the widest, easiest gap
export const PIPE_GAP_MIN = 110; // NARROWEST gap; each pipe's gap is random in [min, max]
export const PIPE_SPACING = 260; // horizontal distance between pipes (px)
export const WORLD_HEIGHT = 640; // logical play area height
export const BIRD_X = 120; // fixed horizontal position of the bird
export const BIRD_RADIUS = 15;
export const PIPE_HALF_WIDTH = 26;
export const PIPE_COUNT = 40; // pipes in the course (also the win/progress denom)
/** The first pipe's x — gives the player a moment before the first obstacle. */
export const FIRST_PIPE_X = 520;
/**
 * A flap event: milliseconds since the run started (client clock). Carries the
 * bird's current altitude `y` too, purely so the opponent's ghost has a fresh
 * position without any extra event — flaps are frequent enough to keep it live.
 * `y` is cosmetic (never trusted for scoring; the crash is server-verified).
 */
export const FlapEvent = z.object({
    type: z.literal('flap'),
    tMs: z.number().nonnegative(),
    y: z.number().min(0).max(WORLD_HEIGHT).optional(),
});
/** A crash event: the client detected it hit a pipe/ground at tMs. */
export const CrashEvent = z.object({
    type: z.literal('crash'),
    tMs: z.number().nonnegative(),
    /** Distance (pipes passed) reported at crash, for a fast display. */
    pipesPassed: z.number().int().nonnegative(),
});
export const FlappyEvent = z.discriminatedUnion('type', [FlapEvent, CrashEvent]);
/** Mulberry32 — tiny deterministic PRNG so client & server build identical courses. */
function prng(seed) {
    let a = seed >>> 0;
    return () => {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
function seedToInt(seed) {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i++) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}
/**
 * Deterministic pipe layout from a seed. Shared by the client (which renders and
 * simulates locally) and the server (which replays to verify), so both build the
 * IDENTICAL course from the same seed.
 */
export function buildCourse(seed, count) {
    const rng = prng(seedToInt(`${seed}:flappy-course`));
    const pipes = [];
    // Consecutive gap centres move by at most MAX_STEP, so the next gap is always
    // reachable from the current one within one pipe spacing — a fair, winnable
    // course rather than random far-apart gaps that no bird could thread.
    const MAX_STEP = 90;
    let gapCenterY = WORLD_HEIGHT / 2;
    for (let i = 0; i < count; i++) {
        // Each pipe gets its own gap HEIGHT, random in [min, max]; a narrower gap is
        // a harder pipe. The first two pipes always use the full (easiest) gap to
        // ease the player in.
        const gap = i < 2 ? PIPE_GAP : PIPE_GAP_MIN + rng() * (PIPE_GAP - PIPE_GAP_MIN);
        // Keep the whole gap on-screen: clamp the centre to this pipe's own margins.
        const margin = gap / 2 + 30;
        const lo = margin;
        const hi = WORLD_HEIGHT - margin;
        if (i > 0) {
            const delta = (rng() * 2 - 1) * MAX_STEP;
            gapCenterY += delta;
        }
        gapCenterY = Math.max(lo, Math.min(hi, gapCenterY));
        pipes.push({ x: FIRST_PIPE_X + i * PIPE_SPACING, gapCenterY, gap });
    }
    return pipes;
}
//# sourceMappingURL=flappy.js.map